/**
 * Merch Optimization Agent - Combined with Planogram Labs
 * ========================================================
 * Unified merchandising hub: assortment optimization, planogram simulation,
 * SKU recommendations, shelf visualization, and $/inch performance metrics.
 * 
 * Clean, polished design following PIQ standards (ML Command Center style)
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import GlobalHeader from '../../components/GlobalHeader';
import ExampleQuestions from '@/components/agents/ExampleQuestions';
import { 
  Loader2, 
  Package, 
  TrendingUp, 
  TrendingDown, 
  Grid3X3, 
  Layers, 
  RefreshCw, 
  ArrowUpRight, 
  ArrowDownRight,
  BarChart3,
  Sparkles,
  X,
  AlertCircle,
  LayoutGrid,
  Clock,
  Rocket,
  CheckCircle2,
  Zap,
  Brain,
  ChevronRight,
  Target,
  DollarSign,
  ShoppingBag,
  Store
} from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';

// Example questions for Merch Optimization
const EXAMPLE_QUESTIONS = [
  "Which SKUs should we cut vs expand at Ulta?",
  "What would you recommend for assortment by region?",
  "What if we gave our top SKU 20% more space?",
  "How should we allocate space across store clusters?",
  "What's our $/inch performance by SKU?"
];

// Interfaces for assortment data
interface AssortmentRec {
  recommendation_type: string;
  sku: string;
  sku_description: string;
  current_store_count: number;
  recommended_store_count: number;
  projected_revenue_impact: number;
  confidence_score: number;
  reasoning: string;
}

interface SKURec {
  sku: string;
  description: string;
  currentRevenue: number;
  yoyChange: string;
  storeCount: number;
  recommendation: string;
  reasoning: string;
}

interface ClusterRec {
  clusterId: string;
  clusterName: string;
  storeCount: number;
  totalRevenue: number;
  revenueShare: string;
  recommendedSpaceShare: string;
  action: string;
}

interface CategoryPerf {
  category: string;
  skuCount: number;
  totalRevenue: number;
  totalUnits: number;
  avgStoreCount: number;
  revenuePerSKU: number;
}

// Interfaces for planogram data
interface PlanogramProduct {
  sku_name: string;
  position: number;
  width: number;
  facings: number;
  total_inches: number;
  current_score: number;
  vs_benchmark: number;
  status: 'above' | 'below';
  color: string;
  trend_data?: number[];
}

interface TrendData {
  weekly_data: Array<{
    week: number;
    score: number;
    dollars_per_inch: number;
    revenue: number;
    units: number;
  }>;
  trend: {
    direction: string;
    change_percent: string;
  };
  weeks_of_data: number;
}

const MerchOptimization: React.FC = () => {
  const { selectedRetailer } = useTenant();
  const [activeTab, setActiveTab] = useState('assortment');
  
  // Assortment data state
  const [assortmentRecs, setAssortmentRecs] = useState<AssortmentRec[]>([]);
  const [cutCandidates, setCutCandidates] = useState<SKURec[]>([]);
  const [addCandidates, setAddCandidates] = useState<SKURec[]>([]);
  const [clusterRecs, setClusterRecs] = useState<ClusterRec[]>([]);
  const [categories, setCategories] = useState<CategoryPerf[]>([]);
  
  // Planogram data state
  const [planogramProducts, setPlanogramProducts] = useState<PlanogramProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<PlanogramProduct | null>(null);
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [loadingTrend, setLoadingTrend] = useState(false);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [planogramLoading, setPlanogramLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Check if we have real data
  const hasRealData = assortmentRecs.length > 0 || cutCandidates.length > 0 || addCandidates.length > 0 || 
                       clusterRecs.length > 0 || categories.length > 0 || planogramProducts.length > 0;

  const fetchAssortmentData = async () => {
    if (!selectedRetailer?.id) return;
    
    try {
      const [assortRes, skuRes, clusterRes, catRes] = await Promise.all([
        fetch(`/api/merch-optimization/assortment?retailer_id=${selectedRetailer.id}`).then(r => r.json()),
        fetch(`/api/merch-optimization/sku-recommendations?retailer_id=${selectedRetailer.id}`).then(r => r.json()),
        fetch(`/api/merch-optimization/clusters?retailer_id=${selectedRetailer.id}`).then(r => r.json()),
        fetch(`/api/merch-optimization/categories?retailer_id=${selectedRetailer.id}`).then(r => r.json()),
      ]);

      if (assortRes.success) setAssortmentRecs(assortRes.recommendations || []);
      if (skuRes.success) {
        setCutCandidates(skuRes.cutCandidates || []);
        setAddCandidates(skuRes.addCandidates || []);
      }
      if (clusterRes.success) setClusterRecs(clusterRes.clusters || []);
      if (catRes.success) setCategories(catRes.categories || []);

      console.log('[MerchOptimization] Assortment data loaded');
    } catch (error) {
      console.error("[MerchOptimization] Failed to fetch assortment data:", error);
    }
  };

  const fetchPlanogramData = async () => {
    setPlanogramLoading(true);
    try {
      const response = await fetch('/api/planogram/layout?period=last-12');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPlanogramProducts(data.products || []);
        }
      }
      console.log('[MerchOptimization] Planogram data loaded');
    } catch (error) {
      console.error('[MerchOptimization] Failed to load planogram:', error);
    } finally {
      setPlanogramLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchAssortmentData(), fetchPlanogramData()]);
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  const loadTrendData = async (product: PlanogramProduct) => {
    setLoadingTrend(true);
    setSelectedProduct(product);
    setTrendData(null);
    
    try {
      const response = await fetch(`/api/planogram/trends?sku=${encodeURIComponent(product.sku_name)}&weeks=12`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTrendData(data);
        }
      }
    } catch (error) {
      console.error('Failed to load trend:', error);
    } finally {
      setLoadingTrend(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [selectedRetailer?.id]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'PRIORITIZE':
        return <Badge className="bg-green-500 hover:bg-green-600">Prioritize</Badge>;
      case 'REDUCE':
        return <Badge className="bg-red-500 hover:bg-red-600">Reduce</Badge>;
      case 'MAINTAIN':
        return <Badge className="bg-blue-500 hover:bg-blue-600">Maintain</Badge>;
      case 'expand':
        return <Badge className="bg-green-500 hover:bg-green-600">Expand</Badge>;
      case 'contract':
        return <Badge className="bg-orange-500 hover:bg-orange-600">Contract</Badge>;
      case 'EXIT':
        return <Badge className="bg-red-500 hover:bg-red-600">Exit</Badge>;
      case 'EXPAND':
        return <Badge className="bg-green-500 hover:bg-green-600">Expand</Badge>;
      default:
        return <Badge>{action}</Badge>;
    }
  };

  const getAIInsight = (trend: TrendData['trend'] | undefined) => {
    if (!trend) return '';
    
    const change = parseFloat(trend.change_percent);
    
    if (Math.abs(change) < 5) {
      return '→ Stable performance - no significant changes detected. Continue current strategy.';
    } else if (change > 15) {
      return '↑ Strong improvement detected! Performance trending upward - maximize this momentum.';
    } else if (change > 5) {
      return '↗ Gradual improvement observed. Monitor to ensure trend continues positively.';
    } else if (change < -15) {
      return '↓ Significant decline detected. Investigate potential causes: stock-outs, planogram changes, or competitive pressure.';
    } else {
      return '↘ Slight decline noted. Monitor closely and consider corrective actions if trend continues.';
    }
  };

  const totalShelfWidth = planogramProducts.reduce((sum, p) => sum + p.total_inches, 0);

  // Quick stats for header
  const totalRevenue = categories.reduce((sum, c) => sum + c.totalRevenue, 0);
  const totalSKUs = categories.reduce((sum, c) => sum + c.skuCount, 0);
  const avgDollarsPerInch = planogramProducts.length > 0 
    ? planogramProducts.reduce((sum, p) => sum + p.current_score, 0) / planogramProducts.length 
    : 0;

  // Connected agents
  const connectedAgents = [
    { title: 'Retail Story', href: '/agents/retail-story', icon: ShoppingBag, gradient: 'from-blue-500 to-cyan-600' },
    { title: 'Inventory Intelligence', href: '/ai/inventory-intelligence', icon: Package, gradient: 'from-purple-500 to-violet-600' },
    { title: 'Supply Chain', href: '/agents/supply-chain', icon: Store, gradient: 'from-amber-500 to-orange-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader 
        title="Merch Optimization & Planogram"
        subtitle="Assortment optimization, shelf visualization, space allocation & SKU performance"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Status Banner */}
        <div className="rounded-2xl shadow-lg border-2 bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 border-orange-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 shadow-lg">
                  <Package className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-gray-900">Merch Intelligence</h2>
                    <Badge className={`${hasRealData ? 'bg-green-100 text-green-800 border-green-300' : 'bg-orange-100 text-orange-800 border-orange-300'} text-sm px-3 py-1`}>
                      {hasRealData ? 'Active' : 'Coming Soon'}
                    </Badge>
                  </div>
                  <p className="text-gray-600 max-w-xl">
                    AI-powered assortment optimization, space allocation analysis, shelf visualization, 
                    and SKU performance metrics powered by your actual sales data.
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={handleRefresh} 
                disabled={loading || refreshing}
                className="bg-white hover:bg-gray-50 shadow-sm"
              >
                {refreshing || loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh Data
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-blue-700">Total SKUs</span>
              <div className="p-3 bg-blue-500 rounded-xl shadow-md">
                <Grid3X3 className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-blue-700 mb-2">{totalSKUs || planogramProducts.length || '—'}</div>
            <div className="flex items-center gap-1 text-sm text-blue-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">{totalSKUs > 0 ? 'In portfolio' : 'Awaiting data'}</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-emerald-700">Total Revenue</span>
              <div className="p-3 bg-emerald-500 rounded-xl shadow-md">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-emerald-700 mb-2">{totalRevenue > 0 ? formatCurrency(totalRevenue) : '—'}</div>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">{totalRevenue > 0 ? 'This period' : 'Awaiting data'}</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100/50 border border-green-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-green-700">Expand Candidates</span>
              <div className="p-3 bg-green-500 rounded-xl shadow-md">
                <ArrowUpRight className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-green-700 mb-2">{addCandidates.length || '—'}</div>
            <div className="flex items-center gap-1 text-sm text-green-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">{addCandidates.length > 0 ? 'Growth opportunities' : 'Awaiting analysis'}</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100/50 border border-red-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-red-700">Exit Candidates</span>
              <div className="p-3 bg-red-500 rounded-xl shadow-md">
                <ArrowDownRight className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-red-700 mb-2">{cutCandidates.length || '—'}</div>
            <div className="flex items-center gap-1 text-sm text-red-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">{cutCandidates.length > 0 ? 'Review needed' : 'Awaiting analysis'}</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-purple-700">Avg $/Inch</span>
              <div className="p-3 bg-purple-500 rounded-xl shadow-md">
                <LayoutGrid className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-purple-700 mb-2">{avgDollarsPerInch > 0 ? `$${avgDollarsPerInch.toFixed(2)}` : '—'}</div>
            <div className="flex items-center gap-1 text-sm text-purple-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">{avgDollarsPerInch > 0 ? 'Shelf efficiency' : 'Awaiting data'}</span>
            </div>
          </div>
        </div>

        {/* Main Intelligence Card */}
        <Card className="border-2 border-gray-200 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl shadow-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Merch Intelligence Hub</h3>
                <p className="text-slate-300">Powered by your sales data • Real-time assortment optimization</p>
              </div>
            </div>
          </div>
          
          <CardContent className="p-8">
            {/* Capabilities Grid */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Rocket className="h-5 w-5 text-orange-500" />
                What you'll get when activated
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-5 rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-blue-100">
                      <Grid3X3 className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Assortment Optimization</h5>
                      <p className="text-sm text-gray-600">SKU-level expand/contract recommendations based on performance</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                <div className="p-5 rounded-xl border-2 border-emerald-100 bg-gradient-to-br from-emerald-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-emerald-100">
                      <LayoutGrid className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Shelf Visualization</h5>
                      <p className="text-sm text-gray-600">Interactive planogram with $/inch performance metrics</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                <div className="p-5 rounded-xl border-2 border-amber-100 bg-gradient-to-br from-amber-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-amber-100">
                      <Layers className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Cluster Analysis</h5>
                      <p className="text-sm text-gray-600">Space allocation recommendations by store cluster</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                <div className="p-5 rounded-xl border-2 border-purple-100 bg-gradient-to-br from-purple-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-purple-100">
                      <BarChart3 className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Category Performance</h5>
                      <p className="text-sm text-gray-600">Revenue/SKU metrics and category mix optimization</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl p-6 border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-orange-500" />
                How Merch Intelligence Works
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    1
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Analyze Portfolio</h5>
                    <p className="text-sm text-gray-600">AI scans SKU performance, velocity, and store coverage</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    2
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Optimize Space</h5>
                    <p className="text-sm text-gray-600">Calculate $/inch and recommend shelf allocations</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    3
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Drive Growth</h5>
                    <p className="text-sm text-gray-600">Execute recommendations and track revenue impact</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Example Questions */}
        <ExampleQuestions 
          questions={EXAMPLE_QUESTIONS}
          accentColor="orange"
          title="Ask the Merch Optimization Agent"
          description="Click any question to optimize assortment, space, and shelf performance"
        />

        {/* Tabs with Real Data */}
        {loading ? (
          <div className="flex items-center justify-center h-64 bg-white rounded-2xl border border-gray-200">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            <span className="ml-3 text-gray-600">Loading merch data...</span>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 bg-white border border-gray-200 rounded-xl p-1">
              <TabsTrigger value="assortment" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
                <Grid3X3 className="h-4 w-4" />
                Assortment
              </TabsTrigger>
              <TabsTrigger value="planogram" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
                <LayoutGrid className="h-4 w-4" />
                Shelf View
              </TabsTrigger>
              <TabsTrigger value="clusters" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
                <Layers className="h-4 w-4" />
                Clusters
              </TabsTrigger>
              <TabsTrigger value="categories" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
                <BarChart3 className="h-4 w-4" />
                Categories
              </TabsTrigger>
            </TabsList>

            {/* ASSORTMENT TAB */}
            <TabsContent value="assortment" className="space-y-6">
              {/* Assortment Recommendations */}
              <Card className="border-2 border-gray-200 shadow-sm">
                <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                  <CardTitle className="flex items-center gap-2">
                    <Grid3X3 className="h-5 w-5 text-orange-500" />
                    Assortment Recommendations
                  </CardTitle>
                  <CardDescription>SKU expansion and contraction opportunities based on performance</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  {assortmentRecs.length > 0 ? (
                    <div className="space-y-3">
                      {assortmentRecs.slice(0, 5).map((rec, idx) => (
                        <div key={idx} className="flex items-start justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {getActionBadge(rec.recommendation_type)}
                              <span className="font-medium">{rec.sku}</span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{rec.sku_description}</p>
                            <p className="text-xs text-gray-500 mt-1">{rec.reasoning}</p>
                          </div>
                          <div className="text-right ml-4">
                            <div className={`font-semibold ${rec.projected_revenue_impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {rec.projected_revenue_impact >= 0 ? '+' : ''}{formatCurrency(rec.projected_revenue_impact)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {rec.current_store_count} → {rec.recommended_store_count} stores
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Assortment Recommendations Yet</h3>
                      <p className="text-gray-500 max-w-md mx-auto">Data will appear once SKU performance is analyzed. Ensure you have retailer data loaded.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* SKU Cut/Add Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Add Candidates */}
                <Card className="border-2 border-green-200 shadow-sm">
                  <CardHeader className="border-b border-green-100 bg-green-50/50">
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <ArrowUpRight className="h-5 w-5" />
                      SKUs to Expand ({addCandidates.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {addCandidates.length > 0 ? (
                      <div className="space-y-2">
                        {addCandidates.slice(0, 5).map((sku, idx) => (
                          <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex justify-between">
                              <span className="font-medium text-sm">{sku.sku}</span>
                              <span className="text-green-600 text-sm font-medium">{sku.yoyChange}</span>
                            </div>
                            <p className="text-xs text-gray-600 truncate mt-1">{sku.description}</p>
                            <p className="text-xs text-gray-500">{formatCurrency(sku.currentRevenue)} | {sku.storeCount} stores</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4">No expansion candidates found yet.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Cut Candidates */}
                <Card className="border-2 border-red-200 shadow-sm">
                  <CardHeader className="border-b border-red-100 bg-red-50/50">
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <ArrowDownRight className="h-5 w-5" />
                      SKUs to Exit ({cutCandidates.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {cutCandidates.length > 0 ? (
                      <div className="space-y-2">
                        {cutCandidates.slice(0, 5).map((sku, idx) => (
                          <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                            <div className="flex justify-between">
                              <span className="font-medium text-sm">{sku.sku}</span>
                              <span className="text-red-600 text-sm font-medium">{sku.yoyChange}</span>
                            </div>
                            <p className="text-xs text-gray-600 truncate mt-1">{sku.description}</p>
                            <p className="text-xs text-gray-500">{formatCurrency(sku.currentRevenue)} | {sku.storeCount} stores</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4">No exit candidates found yet.</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* PLANOGRAM / SHELF VIEW TAB */}
            <TabsContent value="planogram" className="space-y-6">
              {/* Info Banner */}
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 border-2 border-purple-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-purple-100">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-900 mb-1">Interactive Shelf Visualization</h3>
                    <p className="text-sm text-purple-700">
                      Products displayed proportionally to actual shelf space. Color-coded by performance vs $2.64 benchmark.
                      <strong className="ml-1">Double-click</strong> any product to see 12-week trends.
                    </p>
                  </div>
                </div>
              </div>

              {planogramLoading ? (
                <div className="flex items-center justify-center h-64 bg-white rounded-xl border border-gray-200">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
                </div>
              ) : planogramProducts.length === 0 ? (
                <Card className="border-2 border-dashed border-gray-300">
                  <CardContent className="py-16 text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2 text-gray-900">No Planogram Data</h3>
                    <p className="text-gray-500 mb-4">Add product dimensions to see your shelf visualization</p>
                    <Button variant="outline" asChild>
                      <a href="/product-dimensions">Add Product Dimensions</a>
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {/* Shelf Container */}
                  <Card className="border-2 border-gray-200">
                    <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                      <div className="flex items-center justify-between">
                        <CardTitle>Your Shelf ({planogramProducts.length} SKUs)</CardTitle>
                        <span className="text-sm text-gray-500">
                          Total: {totalShelfWidth.toFixed(1)} linear inches
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      {/* Product Boxes */}
                      <div className="flex gap-2 items-end min-h-[280px] bg-gray-100 p-4 rounded-lg overflow-x-auto">
                        {planogramProducts.map((product, idx) => {
                          const widthPercent = (product.total_inches / totalShelfWidth) * 100;
                          const isAbove = product.status === 'above';
                          
                          return (
                            <div
                              key={idx}
                              className={`relative flex-shrink-0 bg-white rounded-lg border-4 transition-all cursor-pointer hover:scale-105 hover:z-10 shadow-sm ${
                                isAbove ? 'border-green-500' : 'border-red-500'
                              }`}
                              style={{
                                width: `${Math.max(widthPercent, 8)}%`,
                                minWidth: '80px',
                                height: '240px'
                              }}
                              onDoubleClick={() => loadTrendData(product)}
                              title="Double-click to see trends"
                            >
                              {/* Performance Badge */}
                              <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold ${
                                isAbove ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                              }`}>
                                ${product.current_score.toFixed(2)}
                              </div>

                              {/* Product Info */}
                              <div className="flex flex-col items-center justify-center h-full p-3 text-center">
                                <div className="text-xs font-semibold mb-2 line-clamp-2">
                                  {product.sku_name}
                                </div>
                                
                                <div className={`text-3xl mb-2 ${isAbove ? 'text-green-500' : 'text-red-500'}`}>
                                  {isAbove ? <TrendingUp className="h-8 w-8" /> : <TrendingDown className="h-8 w-8" />}
                                </div>
                                
                                <div className="text-xs text-gray-500">
                                  {product.total_inches.toFixed(1)}"
                                </div>
                                
                                <div className={`text-xs font-semibold mt-2 ${
                                  isAbove ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {product.vs_benchmark > 0 ? '+' : ''}{product.vs_benchmark}%
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Legend */}
                  <div className="flex items-center justify-center gap-8 p-4 bg-white rounded-xl border border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded"></div>
                      <span className="text-sm text-gray-600">Above $2.64 Benchmark</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded"></div>
                      <span className="text-sm text-gray-600">Below $2.64 Benchmark</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      💡 Double-click any product for historical trends
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* CLUSTERS TAB */}
            <TabsContent value="clusters" className="space-y-6">
              <Card className="border-2 border-gray-200">
                <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="h-5 w-5 text-orange-500" />
                    Cluster Space Allocation
                  </CardTitle>
                  <CardDescription>Store cluster performance and recommended space allocation</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  {clusterRecs.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left p-3 font-medium text-gray-700">Cluster</th>
                            <th className="text-right p-3 font-medium text-gray-700">Stores</th>
                            <th className="text-right p-3 font-medium text-gray-700">Revenue</th>
                            <th className="text-right p-3 font-medium text-gray-700">Current Share</th>
                            <th className="text-right p-3 font-medium text-gray-700">Recommended</th>
                            <th className="text-center p-3 font-medium text-gray-700">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clusterRecs.map((cluster, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="p-3 font-medium">{cluster.clusterName || cluster.clusterId}</td>
                              <td className="p-3 text-right">{cluster.storeCount}</td>
                              <td className="p-3 text-right">{formatCurrency(cluster.totalRevenue)}</td>
                              <td className="p-3 text-right">{cluster.revenueShare}</td>
                              <td className="p-3 text-right">{cluster.recommendedSpaceShare}</td>
                              <td className="p-3 text-center">{getActionBadge(cluster.action)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Layers className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Cluster Data Available</h3>
                      <p className="text-gray-500 mb-4">Run store clustering analysis to see recommendations.</p>
                      <Button variant="outline" asChild>
                        <a href="/ai/store-clusters">Go to Store Clusters</a>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* CATEGORIES TAB */}
            <TabsContent value="categories" className="space-y-6">
              <Card className="border-2 border-gray-200">
                <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-orange-500" />
                    Category Performance Matrix
                  </CardTitle>
                  <CardDescription>Performance overview across all categories</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  {categories.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left p-3 font-medium text-gray-700">Category</th>
                            <th className="text-right p-3 font-medium text-gray-700">SKUs</th>
                            <th className="text-right p-3 font-medium text-gray-700">Revenue</th>
                            <th className="text-right p-3 font-medium text-gray-700">Units</th>
                            <th className="text-right p-3 font-medium text-gray-700">Rev/SKU</th>
                            <th className="text-right p-3 font-medium text-gray-700">Avg Stores</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categories.slice(0, 10).map((cat, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="p-3 font-medium">{cat.category}</td>
                              <td className="p-3 text-right">{cat.skuCount}</td>
                              <td className="p-3 text-right">{formatCurrency(cat.totalRevenue)}</td>
                              <td className="p-3 text-right">{cat.totalUnits.toLocaleString()}</td>
                              <td className="p-3 text-right">{formatCurrency(cat.revenuePerSKU)}</td>
                              <td className="p-3 text-right">{cat.avgStoreCount.toFixed(0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Category Data Available</h3>
                      <p className="text-gray-500">Category performance will appear once data is loaded.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Connected Agents */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Connected Agents</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {connectedAgents.map((agent) => {
              const Icon = agent.icon;
              return (
                <Link key={agent.href} href={agent.href}>
                  <div className="group bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-lg hover:border-orange-300 transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${agent.gradient} shadow-md`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-semibold text-gray-900">{agent.title}</span>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Roadmap Preview */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Merch Intelligence Roadmap</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 shadow-sm">
                  <Grid3X3 className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Assortment AI</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">AI-driven assortment optimization</p>
              <Badge variant="outline" className="text-xs text-green-600 border-green-300">Active</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-sm">
                  <LayoutGrid className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Planogram Sim</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">What-if scenario planning</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-sm">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Regional Tactics</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Region-specific strategies</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 shadow-sm">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Auto-Recommend</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Proactive optimization alerts</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>
          </div>
        </div>

      </div>

      {/* Trend Modal */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedProduct(null)}
        >
          <div 
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{selectedProduct.sku_name}</h3>
                <p className="text-sm text-gray-600 mt-1">12-Week Performance Trend</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedProduct(null)}
              >
                <X className="w-6 h-6" />
              </Button>
            </div>

            {/* Current Score */}
            <div className="p-6 bg-gray-100">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">Current Score</div>
                  <div className={`text-3xl font-bold ${selectedProduct.status === 'above' ? 'text-green-500' : 'text-red-500'}`}>
                    ${selectedProduct.current_score.toFixed(2)}/inch
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">vs Benchmark</div>
                  <div className={`text-3xl font-bold ${selectedProduct.vs_benchmark >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {selectedProduct.vs_benchmark > 0 ? '+' : ''}{selectedProduct.vs_benchmark.toFixed(0)}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">Shelf Space</div>
                  <div className="text-3xl font-bold text-purple-500">
                    {selectedProduct.total_inches.toFixed(1)}"
                  </div>
                </div>
              </div>
            </div>

            {/* Trend Chart and Data */}
            {loadingTrend ? (
              <div className="p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-orange-500" />
                <p className="text-gray-600 mt-2">Loading historical data...</p>
              </div>
            ) : trendData?.weekly_data ? (
              <div className="p-6 space-y-6">
                {/* AI Insights */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-purple-900 mb-1">PatternIQ AI Insight</h4>
                      <p className="text-sm text-purple-700">
                        {getAIInsight(trendData.trend)}
                      </p>
                      <div className="mt-2 text-xs text-purple-600">
                        {trendData.trend.direction === 'improving' ? '📈' : 
                         trendData.trend.direction === 'declining' ? '📉' : '➡️'}
                        {' '}Change: {trendData.trend.change_percent}% over {trendData.weeks_of_data} weeks
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bar Chart */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold mb-4 text-gray-900">Weekly Performance Trend</h4>
                  <div className="space-y-2">
                    {trendData.weekly_data.map((week, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="text-xs text-gray-500 w-12">WK{week.week.toString().substring(4)}</div>
                        <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${week.score >= 2.64 ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min((week.score / 5) * 100, 100)}%` }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold">
                            ${week.score.toFixed(2)}/inch
                          </div>
                        </div>
                        <div className={`text-xs font-semibold w-16 text-right ${week.score >= 2.64 ? 'text-green-600' : 'text-red-600'}`}>
                          {week.score >= 2.64 ? '↑' : '↓'} {((week.score / 2.64 - 1) * 100).toFixed(0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 text-center text-xs text-gray-500">
                    Reference line: $2.64 benchmark (100%)
                  </div>
                </div>

                {/* Weekly Data Table */}
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Week</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Score</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">$/inch</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Revenue</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Units</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {trendData.weekly_data.map((week, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900">WK{week.week.toString().substring(4)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            ${week.score.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">
                            ${week.dollars_per_inch.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">
                            ${week.revenue.toFixed(0)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">
                            {week.units}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="p-12 text-center">
                <p className="text-gray-500 mb-4">No historical data available yet</p>
                <p className="text-sm text-gray-400">Data accumulates as you view shelf performance over time</p>
              </div>
            )}

            {/* Close Button */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <Button
                onClick={() => setSelectedProduct(null)}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchOptimization;
